import type { CameraSettings } from '../camera/ThirdPersonCamera';
import type { GraphicSettings } from '../core/SettingsStore';
import { STAGES } from '../stages/stages';

export interface PauseMenuCallbacks {
  onResume: () => void;
  onSettingsChange: (patch: Partial<CameraSettings>) => void;
  /** Tombol RESET = ulangi stage dari awal. */
  onRestartStage: () => void;
  onGraphicChange: (patch: Partial<GraphicSettings>) => void;
  onQuitToMenu: () => void;
  /** Host multiplayer memilih stage dari menu pause. */
  onStageSelect?: (index: number) => void;
}

type RangeKey = 'distance' | 'heightOffset' | 'offsetX' | 'offsetY';
type CheckKey = 'invertX' | 'invertY';
type SelectKey = 'trailMode' | 'slimeStyle';
type GraphicCheckKey = 'debugHud';

interface RangeField {
  key: RangeKey;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
}

const CHECKBOX_FIELDS: Array<{ key: CheckKey; label: string }> = [
  { key: 'invertX', label: 'Invert horizontal (X)' },
  { key: 'invertY', label: 'Invert vertical (Y)' },
];

const HUD_CHECKBOX_FIELDS: Array<{ key: GraphicCheckKey; label: string }> = [
  { key: 'debugHud', label: 'Debug HUD (STATE / FPS / NET)' },
];

const RANGE_FIELDS: RangeField[] = [
  { key: 'offsetX', label: 'Geser kamera X', min: -2, max: 2, step: 0.05, unit: ' m' },
  { key: 'offsetY', label: 'Geser kamera Y', min: -2, max: 2, step: 0.05, unit: ' m' },
  { key: 'distance', label: 'Jarak kamera', min: 1.5, max: 10, step: 0.1, unit: ' m' },
  { key: 'heightOffset', label: 'Tinggi kamera', min: 0.2, max: 5, step: 0.1, unit: ' m' },
];

const DECIMALS: Record<RangeKey, number> = {
  distance: 1,
  heightOffset: 1,
  offsetX: 2,
  offsetY: 2,
};

const SELECT_FIELDS: Array<{ key: SelectKey; label: string; options: Array<[string, string]> }> = [
  {
    key: 'trailMode',
    label: 'Jejak',
    options: [
      ['flat', 'Flat'],
      ['3d', '3D transparan'],
    ],
  },
  {
    key: 'slimeStyle',
    label: 'Gaya slime',
    options: [
      ['transparent', 'Transparan'],
      ['cartoon', 'Kartun padat'],
    ],
  },
];

export class PauseMenu {
  private root: HTMLDivElement;
  private settings: CameraSettings;
  private graphic: GraphicSettings;
  private callbacks: PauseMenuCallbacks;
  private visible = false;
  private checkboxes = new Map<CheckKey, HTMLInputElement>();
  private ranges = new Map<RangeKey, HTMLInputElement>();
  private rangeLabels = new Map<RangeKey, HTMLSpanElement>();
  private selects = new Map<SelectKey, HTMLSelectElement>();
  private hudChecks = new Map<GraphicCheckKey, HTMLInputElement>();

  constructor(
    container: HTMLElement,
    settings: CameraSettings,
    graphic: GraphicSettings,
    callbacks: PauseMenuCallbacks,
  ) {
    this.settings = settings;
    this.graphic = graphic;
    this.callbacks = callbacks;

    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:absolute',
      'inset:0',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'background:rgba(10,10,15,0.72)',
      'color:#e8e8ef',
      'font-family:system-ui,sans-serif',
      'cursor:default',
      'pointer-events:auto',
    ].join(';');
    this.root.innerHTML = this.buildMarkup();
    container.appendChild(this.root);

    for (const field of CHECKBOX_FIELDS) {
      const input = this.query<HTMLInputElement>(`[data-setting="${field.key}"]`);
      this.checkboxes.set(field.key, input);
      input.addEventListener('change', () => {
        this.callbacks.onSettingsChange({ [field.key]: input.checked } as Partial<CameraSettings>);
      });
    }

    for (const field of RANGE_FIELDS) {
      const input = this.query<HTMLInputElement>(`[data-setting="${field.key}"]`);
      this.ranges.set(field.key, input);
      this.rangeLabels.set(field.key, this.query<HTMLSpanElement>(`[data-value="${field.key}"]`));
      input.addEventListener('input', () => {
        const value = Number(input.value);
        this.renderRangeLabel(field.key, value, field.unit);
        this.callbacks.onSettingsChange({ [field.key]: value } as Partial<CameraSettings>);
      });
    }

    for (const field of SELECT_FIELDS) {
      const input = this.query<HTMLSelectElement>(`[data-setting="${field.key}"]`);
      this.selects.set(field.key, input);
      input.addEventListener('change', () => {
        this.callbacks.onGraphicChange({ [field.key]: input.value } as Partial<GraphicSettings>);
      });
    }

    for (const field of HUD_CHECKBOX_FIELDS) {
      const input = this.query<HTMLInputElement>(`[data-setting="${field.key}"]`);
      this.hudChecks.set(field.key, input);
      input.addEventListener('change', () => {
        this.callbacks.onGraphicChange({ [field.key]: input.checked } as Partial<GraphicSettings>);
      });
    }

    this.query<HTMLButtonElement>('[data-action="reset"]').addEventListener('click', () => {
      this.callbacks.onRestartStage();
    });
    this.query<HTMLButtonElement>('[data-action="resume"]').addEventListener('click', () => {
      this.callbacks.onResume();
    });
    this.query<HTMLButtonElement>('[data-action="menu"]').addEventListener('click', () => {
      this.callbacks.onQuitToMenu();
    });
    this.query<HTMLButtonElement>('[data-action="stage"]').addEventListener('click', () => {
      const select = this.query<HTMLSelectElement>('[data-stage-select]');
      this.callbacks.onStageSelect?.(Number(select.value));
    });
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.callbacks.onResume();
    });

    this.sync();
  }

  sync(): void {
    for (const field of CHECKBOX_FIELDS) {
      const input = this.checkboxes.get(field.key);
      if (input) input.checked = this.settings[field.key];
    }
    for (const field of RANGE_FIELDS) {
      const input = this.ranges.get(field.key);
      if (!input) continue;
      const value = this.settings[field.key];
      input.value = String(value);
      this.renderRangeLabel(field.key, value, field.unit);
    }
    for (const field of SELECT_FIELDS) {
      const input = this.selects.get(field.key);
      if (input) input.value = this.graphic[field.key];
    }
    for (const field of HUD_CHECKBOX_FIELDS) {
      const input = this.hudChecks.get(field.key);
      if (input) input.checked = this.graphic[field.key];
    }
  }

  show(): void {
    this.sync();
    this.visible = true;
    this.root.style.display = 'flex';
  }

  hide(): void {
    this.visible = false;
    this.root.style.display = 'none';
  }

  get isVisible(): boolean {
    return this.visible;
  }

  /** Tampilkan/sembunyikan pemilih stage (khusus host MP) + samakan pilihan. */
  setStagePicker(visible: boolean, current: number): void {
    this.query<HTMLElement>('[data-section="stage"]').style.display = visible ? 'block' : 'none';
    if (visible) this.query<HTMLSelectElement>('[data-stage-select]').value = String(current);
  }

  dispose(): void {
    this.root.remove();
  }

  private renderRangeLabel(key: RangeKey, value: number, unit: string): void {
    const el = this.rangeLabels.get(key);
    if (el) el.textContent = `${value.toFixed(DECIMALS[key])}${unit}`;
  }

  private query<T extends HTMLElement>(selector: string): T {
    const el = this.root.querySelector<T>(selector);
    if (!el) throw new Error(`PauseMenu: missing element ${selector}`);
    return el;
  }

  private buildMarkup(): string {
    const checkboxes = CHECKBOX_FIELDS.map(
      (field) => `
        <label class="pb-row">
          <span>${field.label}</span>
          <input type="checkbox" data-setting="${field.key}">
        </label>`,
    ).join('');

    const ranges = RANGE_FIELDS.map(
      (field) => `
        <label class="pb-block">
          <span class="pb-line"><span>${field.label}</span><span data-value="${field.key}"></span></span>
          <input type="range" data-setting="${field.key}" min="${field.min}" max="${field.max}" step="${field.step}">
        </label>`,
    ).join('');

    const selects = SELECT_FIELDS.map(
      (field) => `
        <label class="pb-block">
          <span class="pb-line"><span>${field.label}</span></span>
          <select data-setting="${field.key}">
            ${field.options
              .map(([val, lab]) => `<option value="${val}">${lab}</option>`)
              .join('')}
          </select>
        </label>`,
    ).join('');

    const hudChecks = HUD_CHECKBOX_FIELDS.map(
      (field) => `
        <label class="pb-row">
          <span>${field.label}</span>
          <input type="checkbox" data-setting="${field.key}">
        </label>`,
    ).join('');

    const stageOptions = STAGES.map(
      (def, i) => `<option value="${i}">${i + 1}. ${def.name}</option>`,
    ).join('');

    return `
      <style>
        .pb-panel { pointer-events:auto; width:min(760px,94vw); max-height:92vh; overflow:auto; background:rgba(20,22,32,0.96); border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:20px 22px; box-shadow:0 18px 48px rgba(0,0,0,0.45); }
        .pb-title { margin:0 0 4px; font-size:16px; letter-spacing:3px; }
        .pb-hint { margin:0 0 6px; font-size:12px; opacity:0.55; line-height:1.5; }
        .pb-cols { display:grid; grid-template-columns:1fr 1fr; gap:0 24px; }
        @media (max-width:640px) { .pb-cols { grid-template-columns:1fr; } }
        .pb-section { font-size:11px; letter-spacing:1.5px; opacity:0.55; margin:14px 0 4px; }
        .pb-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:6px 0; font-size:13px; cursor:pointer; }
        .pb-row input { width:16px; height:16px; accent-color:#88c0d0; cursor:pointer; }
        .pb-block { display:block; padding:6px 0; font-size:13px; }
        .pb-line { display:flex; justify-content:space-between; gap:12px; }
        .pb-line > span:last-child { opacity:0.6; font-variant-numeric:tabular-nums; }
        .pb-block input { width:100%; margin-top:4px; accent-color:#88c0d0; cursor:pointer; }
        .pb-block select { width:100%; margin-top:4px; padding:6px 8px; border-radius:6px; border:1px solid rgba(255,255,255,0.14); background:rgba(255,255,255,0.06); color:#e8e8ef; font-size:12px; cursor:pointer; }
        .pb-actions { display:flex; gap:10px; margin-top:20px; }
        .pb-actions button { flex:1; padding:9px 12px; border-radius:8px; border:1px solid rgba(255,255,255,0.12); background:rgba(255,255,255,0.06); color:#e8e8ef; font-size:12px; letter-spacing:1px; cursor:pointer; }
        .pb-actions button[data-action="resume"] { background:#88c0d0; color:#10131a; border-color:#88c0d0; font-weight:600; }
        .pb-stage { width:100%; margin-top:4px; padding:9px 12px; border-radius:8px; border:none; background:#5dffd4; color:#0a0a0f; font-size:12px; font-weight:700; letter-spacing:1px; cursor:pointer; }
      </style>
      <div class="pb-panel">
        <h2 class="pb-title">PAUSED</h2>
        <p class="pb-hint">Geser X/Y memindahkan posisi kamera (bukan membidik). Semua pengaturan realtime.</p>
        <div class="pb-cols">
          <div>
            <div class="pb-section">AIM</div>
            ${checkboxes}
            <div class="pb-section">POSISI KAMERA</div>
            ${ranges}
          </div>
          <div>
            <div class="pb-section">GRAFIK</div>
            ${selects}
            <div class="pb-section">HUD</div>
            ${hudChecks}
            <div data-section="stage" style="display:none;">
              <div class="pb-section">STAGE (HOST)</div>
              <label class="pb-block">
                <span class="pb-line"><span>Pilih level</span></span>
                <select data-stage-select>${stageOptions}</select>
              </label>
              <button type="button" data-action="stage" class="pb-stage">GANTI STAGE</button>
            </div>
          </div>
        </div>
        <div class="pb-actions">
          <button type="button" data-action="reset">RESET</button>
          <button type="button" data-action="menu">MENU</button>
          <button type="button" data-action="resume">LANJUT</button>
        </div>
      </div>
    `;
  }
}